import { RETIRE_SPLIT_RE, input401kName } from "./retirement";
import type { Account } from "./types";

/**
 * Gather controller config — the "how to pull" knowledge that used to live in
 * the finance-gather skill, moved into the app so the Import/Gather page can
 * *drive* Cowork: it knows each account's freshness (asOf) and tells the agent
 * exactly which accounts to refresh, from which source, and how. Cowork reads
 * the live page instead of a manually-uploaded skill doc.
 *
 * Update this file as lessons come in; the page renders it.
 */

/** Fixed Roth share of each 401k total (from the sheet's per-account history).
 * Cowork enters the Empower total; the app splits Roth/Traditional by this. */
export const RETIREMENT_401K_RATIO: Record<string, number> = {
  "Google 401k": 0.357,
  "Anthropic 401k": 0.5,
};

/** Standing operating rules — always shown, always in force. */
export const GATHER_RULES: string[] = [
  "What to refresh. Refresh EVERY Empower account, every run — they're always pulled, no staleness gate (open the console when you want fresh data). For the manual/point accounts (Zillow, crypto, Lively, CB&T), only refresh the ones flagged 'needs update'; leave the rest. Each account row below shows its own status.",
  "Balances are always an array of {date, institution, owner, type, account, balance} rows (liabilities negative). Usually that's just one snapshot per account, dated today (PT) — the incremental case, when the app is already current. When the app is behind by more than a day (or an account has a gap), pull that account's daily history (getHistories, intervalType=DAY) from its last stored date forward to fill the gap, one row per real day. Only submit days Empower ACTUALLY observed: stop at each account's own last-updated date — never carry-forward, zero-fill, or extend rows past it (those are placeholders, and the app's as-of carry already holds the last real value). Re-submitting TODAY's balance is expected: a later pull's number replaces the earlier one for that day (intraday → market close) — the only-new rule applies to transactions, never balances.",
  "Balances are faithful passthrough. Submit the raw number (liabilities negative) — the app does the downstream transforms (401k splits, sign conventions).",
  "Transactions: normalize on the fly (the old 'submit exactly as-is' rule is retired — the ledger's history is the authority now, not Empower). Dry-run first, then use the summary's `drift` hints to rewrite rows before the real import: rename new/splinter descriptions to the ledger's canonical label (`seenBefore: true` suggestions are the strongest signal), and set each row's category and tags to the ledger's historical pattern — every suggestion carries the FULL historical breakdown (`categories` and `tagSets`, with shares). A ≥80% share is safe to apply; a genuinely mixed label (no dominant share) means pick per row from the listed options. When no hint is confident, keep Empower's value and move on — recategorization and tag rework are cheap app-side after the fact. Never bulk-rename `ambiguous` (payment-rail) strings.",
  "Submit the whole overlap window shown on the page — stored rows are IMMUTABLE, so a re-send can never overwrite what the ledger already holds. The summary reports `transactionsUnchanged` (re-sends that matched, expected) and `protectedIds` (re-sends that DIFFER and were refused — review those, and re-submit only the genuine Empower corrections via `overrideIds`).",
  "Dates are Pacific-time calendar days. Stamp today's balance snapshots with the current date in America/Los_Angeles, never the UTC date — an evening run (after 5pm PT) that stamps UTC dates the snapshot TOMORROW, creating a phantom next-day balance point. (The server also clamps future-PT dates, but submit them right.)",
  "SPLITS ARE THE APP'S, NOT EMPOWER'S. Submit every transaction as Empower's own single row — the undivided parent, its own id, its full amount. Do NOT read or expand `txn.splits`, and never emit one row per split child: Empower's child GUIDs are unknown to the ledger, so they would import as BRAND-NEW rows alongside the app's existing children and double-count the money. When a transaction is split app-side, the app keeps the un-split parent as a hidden, locked ANCHOR under that same Empower id precisely so a re-pull of the parent is recognized and protected — that is why the parent keeps coming back on every wide-window pull, and it is correct. Such a row appears in the dry run's `protectedIds` (not `updatedIds`); drop it from the new-row set and move on. To change how a split is divided, use the import body's `splits`/`unsplits` — never the transaction feed.",
  "The category you submit is now the one that lands. The server-side recategorization rules were removed in Aug 2026 — nothing rewrites a submitted category any more, so a wrong category is yours to catch from the `drift` hints, not something the app quietly repairs. One case the hints CANNOT resolve, because it turns on amount rather than text: an \"RWC Property Services\" charge under $100 is the AppFolio online-payment FEE (Service Charges/Fees), not rent — the fee and the rent share both the description and the raw `Apf*rwc Property Services` string, so the historical consensus (88% Rent) will steer you wrong on the small ones. Anything at the full rent amount is Rent.",
  "Never fetch `/api/finance?type=transactions` during an import run — the import/dry-run response already echoes the hints, drift, and summary you need. It is the most expensive request in the system (~200 Firestore reads + ~0.44 MB egress per call) and must not be used as a convenience read. If a NON-import workflow genuinely needs whole-ledger state (e.g. measuring app→Empower category divergence), fetch it at most once per session and cache the result — never re-pull the same data within a run.",
];

/** Hard limitations discovered in the field — the agent can't work around these. */
export const GATHER_LIMITATIONS: string[] = [
  "Empower's in-tab data is wiped when the session times out. Pull → build → move the payload OUT of the tab quickly; don't leave it in window vars.",
];

export type SourceKey = "empower" | "zillow" | "crypto" | "lively" | "cbt-heloc";

export interface GatherSource {
  key: SourceKey;
  label: string;
  /** Manual = a step needs Jeff (OTP / native password overlay); agent can't finish alone. */
  manual?: boolean;
  url?: string;
  steps: string[];
}

export const SOURCES: Record<SourceKey, GatherSource> = {
  empower: {
    key: "empower",
    label: "Empower (aggregator)",
    url: "https://ira.empower-retirement.com/participant/#/login",
    steps: [
      "Log in via Chrome autofill (no OTP on a recognized device). Use the login URL above — the old dashboard URL was getting stuck.",
      "Judge each account's freshness from the API's `lastRefreshed` field, not by watching tile spinners — read an account's data only once its lastRefreshed reflects a completed re-aggregation.",
      "Build ONE JSON object `{balances, transactions}` and paste it into the single Empower box below — no per-account boxes, no separate bulk box.",
      "balances: for each Empower account below, submit balance rows from its 'from' date shown in the row (usually just today — one snapshot; more rows only when the app is behind, via getHistories intervalType=DAY). Stop at the account's own last-updated date; skip account-days Empower flags 'accurate data unavailable'. 401k: submit the plan TOTAL to the '… - INPUT' account (the app splits Roth/Traditional by fixed ratio).",
      "transactions: pull getUserTransactions for the window shown in the box below. Each row needs `account`, `status`, `id` (userTransactionId), and its FULL `tags` array — every tag, verbatim.",
      "STATUS IS COPIED, NEVER INFERRED. Read Empower's own status field and pass it through verbatim: `posted`, `pending`, or `unknown` if the pull did not expose it. `unknown` REJECTS the whole batch on purpose — that is the escalation, and it is the correct thing to do. Never guess `posted` to make a row go through: a pending transaction re-posts later under a NEW userTransactionId, so a mislabelled one becomes a permanent duplicate that id-based dedup can never detect. Pending rows are dropped on import and reported back as `transactionsPending` / `pendingIds` — that is expected, not a failure, and those rows will arrive on their own once they post. Do NOT resubmit them and do NOT relabel them.",
      "Resolve custom tags (this is the easy-to-miss step). A transaction's own resolved `tags` object only carries SYSTEM tags (e.g. Reimbursable); CUSTOM user tags — Business, Anthropic Expenses, Google Expenses, … — appear on the row only as ids under `customTags.userTags`. Call getTags ONCE, build an id→name map from customTags.userTags, and merge those resolved names into each row's tags array. Skip this and every custom tag silently drops (the recurring pull's original gap). Category = getCategories[categoryId].name (the UI label), NOT categoryName. Pending rows are dropped server-side.",
    ],
  },
  zillow: {
    key: "zillow",
    label: "Zillow (property values)",
    url: "https://www.zillow.com",
    steps: [
      "Search the property address on Zillow (Jeff's homes are claimed).",
      "Read the 'Estimated market value' (Zestimate) header — that's the balance (positive).",
    ],
  },
  crypto: {
    key: "crypto",
    label: "Crypto sheet",
    steps: [
      "Open the crypto Google Sheet (fileId 1OI3xgYS0k4CxZQy43vtxo79FdWfWdugmpeyOkFzfTTk).",
      "Read cell J10 — the Open Positions market-value total (BTC/ETH/SOL). That's the balance.",
    ],
  },
  lively: {
    key: "lively",
    label: "Lively HSA",
    manual: true,
    url: "https://secure.livelyme.com/investments/dashboard/schwab",
    steps: [
      "livelyme.com → Investments → Charles Schwab Health Savings Brokerage Account. Use the INVESTMENT balance (cash + holdings), not the dashboard 'My Balance' (that's the cash side, reads $0).",
      "⚠️ Needs Jeff: the password fills only via the native right-click 'select password' menu, which automation can't click.",
    ],
  },
  "cbt-heloc": {
    key: "cbt-heloc",
    label: "California Bank & Trust (HELOC + Home Loans, direct)",
    manual: true,
    url: "https://www.calbanktrust.com",
    steps: [
      "calbanktrust.com → Sign in → Personal Banking. Use each entry's Current balance (not Available credit, not Payoff).",
      "These are THREE separate accounts — submit each as its own balance row, NEGATIVE, do NOT sum them: the revolving line → account 'HELOC' (…3319), and the term loans → 'Home Loan 1' (…0509) and 'Home Loan 2' (added Aug 2026; match by the remaining account number).",
      "⚠️ Needs Jeff: login requires an OTP the agent can't enter. Empower's values here are stale manual entries — do not submit them.",
    ],
  },
};

/** Which source an account is pulled from. Most are Empower-aggregated; a few
 * are non-Empower or need a direct (manual) login. */
export function sourceForAccount(a: Account): SourceKey {
  if (a.type === "Real Estate") return "zillow";
  if (a.type === "Crypto") return "crypto";
  if (a.institution === "Lively HSA") return "lively";
  if (a.institution === "California Bank and Trust" && a.type === "Loan") return "cbt-heloc";
  return "empower";
}

/** The balance-row `account` identity to submit. 401k calculated splits
 * (…- Roth / …- Traditional) are fed as one total to the "… - INPUT" account;
 * the app splits them. */
export function submitAccountName(a: Account): string {
  const m = a.name.match(RETIRE_SPLIT_RE);
  if (m && a.type === "Retirement" && /401k/i.test(a.name)) return input401kName(m[1]);
  return a.name;
}

/** Days of transaction overlap to re-pull before the last imported txn — covers
 * posting lag and Empower re-categorization since the previous (periodic) run.
 * The gather page shows the exact start date (latest txn date − this). */
export const TXN_LOOKBACK_BUFFER_DAYS = 7;

/** DISPLAY freshness window (days) per account — how long an account can go
 * unobserved before the Accounts page marks it stale ("needs refresh"). This is
 * purely the user-facing staleness indicator; it does NOT drive what Cowork
 * pulls (see needsRefresh). Each account has its own cadence:
 *   • manual logins (Lively, CB&T HELOC/Home Loan) — 30 days
 *   • dead-but-open cards (Macy's, CB&T card) — 90 days
 *   • Zillow home values — 30 days (slow-moving)
 *   • everything else (Empower-aggregated, crypto) — 7 days */
export function stalenessDays(a: Account): number {
  if (a.institution === "Lively HSA") return 30;
  if (a.institution === "California Bank and Trust" && a.type === "Loan") return 30;
  if (a.institution === "Macy's") return 90;
  if (a.institution === "California Bank and Trust" && a.type === "Credit") return 90;
  if (a.type === "Real Estate") return 30;
  return 7;
}

/** True for accounts with Empower daily balance history (getHistories) — these
 * are BACKFILLED day-by-day from their last-observed date. Non-Empower / point
 * accounts (Zillow, crypto, Lively, CB&T HELOC) instead get a single current
 * value, since they have no reliable daily history. */
export function backfillable(a: Account): boolean {
  return sourceForAccount(a) === "empower";
}

function localDate(iso: string): Date | null {
  const [y, m, d] = iso.split("-").map(Number);
  return y && m && d ? new Date(y, m - 1, d) : null;
}

/** Days since the account's last real observation, or null if never observed. */
export function daysSinceUpdate(a: Account, now: Date): number | null {
  const d = a.asOf ? localDate(a.asOf) : null;
  if (!d) return null;
  return Math.floor((now.getTime() - d.getTime()) / 86_400_000);
}

export function isStale(a: Account, now: Date): boolean {
  const days = daysSinceUpdate(a, now);
  return days === null || days > stalenessDays(a);
}

/** Whether a Cowork gather should refresh this account on THIS run — the gather
 * cadence, distinct from the Accounts-page staleness display (isStale).
 * Empower-native accounts are ALWAYS refreshed: one Empower session re-pulls
 * them all every run, so there's no reason to gate them on staleness — open the
 * console daily and you get daily freshness; skip a day and nothing rots. The
 * manual / direct-login accounts (Lively, CB&T, Zillow, dead cards) are heavier
 * to pull, so they're only surfaced when actually stale, on their own cadence. */
export function needsRefresh(a: Account, now: Date): boolean {
  if (sourceForAccount(a) === "empower") return true;
  return isStale(a, now);
}
